import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { didCancel, task, timeout } from 'ember-concurrency';
import type AjaxService from 'lumen/services/ajax';
import {
  APIGATEWAY,
  DRAWER_IDS,
} from 'lumen/utils/constants/general-constants';

export const SMART_INSIGHTS_LAST_VIEWED_LOCAL_STORAGE_KEY =
  'smartInsightsLastViewed';
import type { LegacyResourceQuery } from '@warp-drive/core-types';
import type { SmartInsightDay } from 'lumen/models';
import type { SmartInsightPriority } from 'lumen/models/smart-insights/smart-insight-day';
import type { SmartInsightEntryType } from 'lumen/models/smart-insights/smart-insight-day-insight-entry';
import type { SmartInsightDayResponseMeta } from 'lumen/serializers/smart-insights/smart-insight-day';
import type { SmartInsightsArchiveReason } from 'lumen/utils/constants/smart-insights';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import type {
  SmartInsightDay as SmartInsightsReactDay,
  SmartInsightsArchivedDay,
  SmartInsightsArchivePayload,
  SmartInsightsData,
  SmartInsightsStatus,
} from 'shared-react/lumen';

// Projects one Ember Data SmartInsightDay record into the plain DTO the
// shared-react package expects. Must carry *raw* template/variables — the
// package re-derives formatted text itself (domain/format-text.ts) using its
// own intl + currency, so passing lumen's already-formatted text here would
// double-format it.
export function projectSmartInsightDay(
  day: SmartInsightDay,
): SmartInsightsReactDay {
  return {
    actionedStatus:
      (day.archiveReason as SmartInsightsReactDay['actionedStatus']) ?? null,
    archivedAt: day.archivedAt,
    generationId: day.generationId,
    insightCreationTime: day.insightCreationTime,
    insightEntries: day.insightEntries.map((entry) => ({
      ...(entry.config?.currency && {
        config: { currency: entry.config.currency },
      }),
      info: {
        connectedDates: entry.info.connectedDates,
        insightElementType: entry.info.insightElementType,
        relevance: entry.info.relevance,
        text: entry.info.text,
      },
    })),
    kpis: day.kpis
      ? {
          adr: day.kpis.adr,
          adrPickupVsYesterday: day.kpis.adrPickupVsYesterday,
          adrStly: day.kpis.adrStly,
          demand: day.kpis.demand,
          lowestRate: day.kpis.lowestRate,
          medianCompsetRate: day.kpis.medianCompsetRate,
          message: day.kpis.message,
          occupancy: day.kpis.occupancy,
          occupancyPickupVs3DaysAgo: day.kpis.occupancyPickupVs3DaysAgo,
          occupancyPickupVs7DaysAgo: day.kpis.occupancyPickupVs7DaysAgo,
          occupancyPickupVsYesterday: day.kpis.occupancyPickupVsYesterday,
          occupancyStly: day.kpis.occupancyStly,
          occupancyStlyPickupVs3DaysAgo: day.kpis.occupancyStlyPickupVs3DaysAgo,
          occupancyStlyPickupVs7DaysAgo: day.kpis.occupancyStlyPickupVs7DaysAgo,
          occupancyStlyPickupVsYesterday:
            day.kpis.occupancyStlyPickupVsYesterday,
          ownRate: day.kpis.ownRate,
          roomNights: day.kpis.roomNights,
          roomNightsPickupVs3DaysAgo: day.kpis.roomNightsPickupVs3DaysAgo,
          roomNightsPickupVs7DaysAgo: day.kpis.roomNightsPickupVs7DaysAgo,
          roomNightsPickupVsYesterday: day.kpis.roomNightsPickupVsYesterday,
          roomNightsStly: day.kpis.roomNightsStly,
          roomNightsStlyPickupVs3DaysAgo:
            day.kpis.roomNightsStlyPickupVs3DaysAgo,
          roomNightsStlyPickupVs7DaysAgo:
            day.kpis.roomNightsStlyPickupVs7DaysAgo,
          roomNightsStlyPickupVsYesterday:
            day.kpis.roomNightsStlyPickupVsYesterday,
        }
      : null,
    meta: {
      biRedirectId: day.meta?.biRedirectId ?? null,
      featureFlags: day.meta?.featureFlags ?? [],
      rateiRedirectId: day.meta?.rateiRedirectId ?? null,
    },
    recommendationEntries: day.recommendationEntries.map((entry) => ({
      info: {
        recommendationElementType: entry.info
          .recommendation_element_type as SmartInsightsReactDay['recommendationEntries'][number]['info']['recommendationElementType'],
        text: {
          template: entry.info.text.template,
          templateKey: entry.info.text.template_key,
          variables: entry.info.text.variables,
        },
      },
    })),
    stayDate: day.stayDate,
    stayDatePriority: day.stayDatePriority,
    stayDateRelevance: day.stayDateRelevance,
    stayDateText: day.stayDateText,
  };
}

export function projectToReactData(days: SmartInsightDay[]): SmartInsightsData {
  const projectedDays = days.map(projectSmartInsightDay);

  return {
    days: projectedDays,
    meta: projectedDays[0]?.meta ?? {
      biRedirectId: null,
      featureFlags: [],
      rateiRedirectId: null,
    },
  };
}

export interface ArchiveUndoCallbacks {
  hotelinfoId?: string | null;
  onNavigate?: (stayDate: string) => void;
  onRefetch?: (stayDate: string, subscriptionId?: string) => Promise<void>;
  product?: string;
}

export function groupFormattedTextByElementType<
  T extends { formattedText: string },
>(
  entries: Iterable<T>,
  getElementType: (entry: T) => string | undefined,
): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  for (const entry of entries) {
    const elementType = getElementType(entry);
    if (!elementType) {
      continue;
    }
    (grouped[elementType] ??= []).push(entry.formattedText);
  }

  return grouped;
}

export default class SmartInsightsService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare drawer: Services['drawer'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked hasArchivedInsightsError = false;
  @tracked hasDismissedNotificationBubble = false;
  @tracked hasInsightsError = false;
  @tracked reactArchivedDay: SmartInsightsArchivedDay | null = null;
  @tracked reactData: SmartInsightsData | null = null;
  @tracked reactStatus: SmartInsightsStatus = 'loading';

  archiveInsightTask = task(
    { drop: true },
    async (
      stayDate: string,
      reason: SmartInsightsArchiveReason,
      subscriptionId?: string,
      undoCallbacks?: ArchiveUndoCallbacks,
      // Set when archiving is triggered from the React mount (PG-2983): the
      // package already built the formatted templates itself (it has its own
      // intl + currency in scope), so use those instead of recomputing them
      // from the Ember records below.
      reactPayload?: SmartInsightsArchivePayload,
    ) => {
      const resolvedSubscriptionId =
        subscriptionId ??
        this.hotelAndMarketService.hotel?.id ??
        this.hotelAndMarketService.market?.id ??
        undefined;

      const queryParams = new URLSearchParams({
        ...(resolvedSubscriptionId && {
          subscription_id: resolvedSubscriptionId,
        }),
        stay_date: stayDate,
      });
      const url = `${this.ajax.host}/${APIGATEWAY}/smartinsights/insights/archive/?${queryParams.toString()}`;

      // Look up the live insight before archiving so we can send a snapshot
      // of its currently-rendered text (PG-2913). Note: as of PG-2984, the
      // archive GET endpoint doesn't echo this back — formattedText always
      // re-derives text from template + variables regardless of archive state.
      const insights = this.getSmartInsightsTask.lastSuccessful?.value ?? [];
      const insightToRemove = insights.find(
        (insight) => insight.stayDate === stayDate,
      );

      const formattedTemplates =
        reactPayload?.formattedTemplates ??
        groupFormattedTextByElementType(
          insightToRemove?.insightEntries ?? [],
          (entry) => entry.info.insightElementType,
        );
      const formattedRecommendationTemplates =
        reactPayload?.formattedRecommendationTemplates ??
        groupFormattedTextByElementType(
          insightToRemove?.recommendationEntries ?? [],
          (entry) => entry.info.recommendation_element_type,
        );

      try {
        await this.ajax.request(url, {
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({
            actioned_status: reason,
            formatted_templates: formattedTemplates,
            formatted_recommendation_templates:
              formattedRecommendationTemplates,
          }),
        });

        const formattedDate = dayjs(stayDate).format('ddd, MMM D');
        const toastId = this.toast.success(
          this.intl.t('smart_insights.archive.toast_success', {
            date: formattedDate,
          }),
          {
            showIcon: true,
            sticky: true,
            dismissible: true,
            action: {
              label: this.intl.t('smart_insights.archive.undo'),
              onClick: () =>
                this.undoArchiveInsightTask.perform(
                  toastId,
                  stayDate,
                  resolvedSubscriptionId,
                  undoCallbacks,
                ),
            },
          },
        );

        // Remove the insight from the store so it disappears from the calendar immediately
        if (insightToRemove) {
          this.store.unloadRecord(insightToRemove);
        }
        this.syncToReact(this.smartInsightDays, this.reactStatus);

        // Re-fetch archived insights to update the archived tab
        void this.getArchivedInsightsTask.perform(
          stayDate,
          resolvedSubscriptionId,
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        this.toast.error(this.intl.t('general.error_short'), {
          showIcon: true,
        });

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getArchivedInsightsTask = task(
    { restartable: true },
    async (stayDate: string, subscriptionId?: string) => {
      this.hasArchivedInsightsError = false;

      const resolvedSubscriptionId =
        subscriptionId ??
        this.hotelAndMarketService.hotel?.id ??
        this.hotelAndMarketService.market?.id;

      // Don't fire the request without a subscription context — there's
      // nothing to fetch, and the in-flight ajax would otherwise leak as
      // an unhandled rejection if the container tears down before it
      // resolves (e.g. during component tests that don't mock this task).
      if (!resolvedSubscriptionId) {
        this.reactArchivedDay = { days: [], status: 'success' };
        return [] as SmartInsightDay[];
      }

      const requestParams: LegacyResourceQuery = {
        namespace: APIGATEWAY,
        typeKey: 'smartinsights/insights/archive',
        subscription_id: resolvedSubscriptionId,
        stay_date: stayDate,
      };

      try {
        const response = await this.store.query<SmartInsightDay>(
          'smart-insights/smart-insight-day',
          requestParams,
        );
        const days = response.slice();

        this.reactArchivedDay = {
          days: projectToReactData(days).days,
          status: 'success',
        };
        return days;
      } catch (error) {
        // Aborts (status 0) cover both XHR.abort() during teardown and
        // network failures (e.g. blocked requests). Treat both as errors —
        // the empty result would otherwise look identical to "no archived
        // insights for this day".
        this.hasArchivedInsightsError = true;
        this.errorTracking.captureException(error);

        this.reactArchivedDay = { days: [], status: 'error' };
        return [] as SmartInsightDay[];
      }
    },
  );

  getSmartInsightsTask = task({ restartable: true }, async () => {
    this.hasDismissedNotificationBubble = false;
    this.hasInsightsError = false;

    const today = dayjs().startOf('day');
    const subscriptionId = this.hotelAndMarketService.hotelOrMarket.id;

    // Important: when adjusting QPs here, it's possible that the source QPs won't match anymore in the SmartInsightDayInsightEntry model.
    // They need to be kept aligned to ensure we can navigate to the correct source for insights.
    const requestParams: LegacyResourceQuery = {
      namespace: APIGATEWAY,
      typeKey: 'smartinsights/insights',
      subscription_id:
        this.hotelAndMarketService.hotel?.id ??
        this.hotelAndMarketService.market?.id,
      extract_date: today.format('YYYY-MM-DD'),
      from_date_start: today.format('YYYY-MM-DD'),
      rate_type: 'lowest',
      ota: 'bookingdotcom',
      // Not used by the backend — included solely to bust the browser cache when the user
      // changes their in-app language. Without this, the cached response (in the old language)
      // would be served instead of fetching fresh translated insights.
      language: this.hotelAndMarketService.user.locale,
    };

    try {
      const MAX_POLLING_ATTEMPTS = 24;

      for (let attempt = 0; attempt < MAX_POLLING_ATTEMPTS; attempt++) {
        // Spread requestParams on each iteration: handleQuery mutates the object by deleting namespace/typeKey.
        const response = await this.store.query<SmartInsightDay>(
          'smart-insights/smart-insight-day',
          { ...requestParams },
        );

        const meta = response.meta as SmartInsightDayResponseMeta;

        if (meta?.isFailed) {
          throw new Error('Smart insights generation failed');
        }

        if (meta?.isGenerating) {
          await timeout((meta.retryAfterSeconds ?? 5) * 1000);
          continue;
        }

        // Assign hotel relationship and meta data to each SmartInsightDay.
        response.forEach((insightDay) => {
          insightDay.hotelinfoId = subscriptionId;
          insightDay.meta = meta;
        });

        // If the drawer is expanded when the insights are successfully fetched, we mark them as seen.
        if (this.drawer.isDrawerExpanded(DRAWER_IDS.SMART_INSIGHTS_DRAWER)) {
          this.storeHasSeenDictInLocalStorage();
        }

        this.syncToReact(response.slice(), 'success');
        return response;
      }

      throw new Error('Smart insights polling exceeded maximum attempts');
    } catch (error) {
      this.hasInsightsError = true;
      this.errorTracking.captureException(error);
      this.syncToReact([], 'error');

      return [] as unknown as Awaited<
        ReturnType<typeof this.store.query<SmartInsightDay>>
      >;
    }
  });

  reloadInsightsTask = task({ drop: true }, async (savedMessage: string) => {
    // The loading toast stays up until closed, which suits a reload that can take a while when insights are regenerated.
    const loadingToastId = this.toast.loading(
      this.intl.t('settings.competitors.reloading_insights'),
      { dismissible: true },
    );

    try {
      await this.getSmartInsightsTask.perform();

      if (this.hasInsightsError) {
        this.toast.error(this.intl.t('general.error_short'), {
          dismissible: true,
          showIcon: true,
        });
      } else {
        this.toast.success(this.intl.t('smart_insights.reload.toast_success'), {
          dismissible: true,
          showIcon: true,
        });
      }
    } catch (error) {
      // A newer fetch (e.g. navigating to another hotel) cancels this one, so the save is still confirmed.
      if (!didCancel(error)) {
        throw error;
      }

      this.toast.success(savedMessage, { dismissible: true, showIcon: true });
    } finally {
      this.toast.close(loadingToastId);
    }
  });
  undoArchiveInsightTask = task(
    { drop: true },
    async (
      toastId: string,
      stayDate: string,
      subscriptionId?: string,
      undoCallbacks?: ArchiveUndoCallbacks,
    ) => {
      const resolvedSubscriptionId =
        subscriptionId ??
        this.hotelAndMarketService.hotel?.id ??
        this.hotelAndMarketService.market?.id ??
        undefined;

      // Show loading state on the undo button
      this.toast.update(toastId, {
        action: {
          label: this.intl.t('smart_insights.archive.undo'),
          onClick: () => {},
          isLoading: true,
          isDisabled: true,
        },
      });

      const queryParams = new URLSearchParams({
        ...(resolvedSubscriptionId && {
          subscription_id: resolvedSubscriptionId,
        }),
        stay_date: stayDate,
      });
      const url = `${this.ajax.host}/${APIGATEWAY}/smartinsights/insights/archive/?${queryParams.toString()}`;

      try {
        await this.ajax.request(url, {
          method: 'DELETE',
          contentType: 'application/json',
        });

        // Dismiss the archive toast and show restored confirmation
        this.toast.close(toastId);
        this.toast.success(
          this.intl.t('smart_insights.archive.undo_toast_success'),
          { showIcon: true },
        );

        this.usageAnalytics.track('Smart Insights Insight Undo Archived', {
          eventTrigger: 'smart-insights-undo-archive-toast',
          product: undoCallbacks?.product ?? 'smart-insights',
          date: stayDate,
          daysInFuture: dayjs(stayDate).diff(dayjs(), 'day'),
          ...(undoCallbacks?.hotelinfoId !== undefined && {
            hotelinfoId: undoCallbacks.hotelinfoId,
          }),
        });

        // Re-fetch both active and archived insights
        if (undoCallbacks?.onRefetch) {
          await undoCallbacks.onRefetch(stayDate, resolvedSubscriptionId);
        } else {
          await this.getSmartInsightsTask.perform();
        }
        void this.getArchivedInsightsTask.perform(
          stayDate,
          resolvedSubscriptionId,
        );

        // Navigate to the restored insight
        undoCallbacks?.onNavigate?.(stayDate);
      } catch (error) {
        this.toast.close(toastId);
        this.toast.error(
          this.intl.t('smart_insights.archive.undo_toast_error'),
          { showIcon: true },
        );
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get archivedInsightDays(): SmartInsightDay[] {
    return this.getArchivedInsightsTask.lastSuccessful?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasInsights() {
    const { smartInsightDays: insights } = this;

    if (!insights) {
      return false;
    }

    return insights.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get leadtime() {
    if (this.access.hasSmartInsightsYearView) {
      return 365;
    }

    return 90;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get localStorageHotelOrMarketId() {
    if (this.hotelAndMarketService.hotel?.id) {
      return `hotel-${this.hotelAndMarketService.hotel.id}`;
    }

    if (this.hotelAndMarketService.market?.id) {
      return `market-${this.hotelAndMarketService.market.id}`;
    }

    return null;
  }

  get smartInsightDays(): SmartInsightDay[] {
    const { getSmartInsightsTask } = this;

    return getSmartInsightsTask.last?.value ?? [];
  }

  getSmartInsightUsageAnalyticsContext(
    smartInsight: SmartInsightDay | null,
    date: string,
  ): {
    date: string;
    daysInFuture: number;
    insightId: string | null;
    insightTypes: SmartInsightEntryType[];
    priority: SmartInsightPriority | null;
    recommendationsText: string[];
    text: string[];
  } {
    const {
      stayDatePriority,
      insightEntriesSortedByRelevance,
      visibleRecommendationEntries,
    } = smartInsight ?? {
      stayDatePriority: null,
      insightEntriesSortedByRelevance: [],
      visibleRecommendationEntries: [],
    };

    const smartInsightText = insightEntriesSortedByRelevance.map(
      (entry) => entry.formattedText,
    );
    const smartInsightRecommendationsText = visibleRecommendationEntries.map(
      (entry) => entry.formattedText,
    );

    return {
      date,
      insightId: smartInsight?.generationId ?? null,
      priority: stayDatePriority,
      recommendationsText: smartInsightRecommendationsText,
      text: smartInsightText,
      daysInFuture: dayjs(date).diff(dayjs(), 'day'),
      insightTypes: insightEntriesSortedByRelevance.map(
        (entry) => entry.info.insightElementType,
      ),
    };
  }

  /**
   * Reloads the insights after a compset save, since the primary compset
   * drives them. Returns false when the hotel has no Smart Insights, so the
   * caller can show its regular success toast instead.
   */
  reloadAfterCompsetSave(savedMessage: string): boolean {
    if (!this.access.hasSmartInsights) {
      return false;
    }

    void this.reloadInsightsTask.perform(savedMessage);

    return true;
  }

  syncToReact(days: SmartInsightDay[], status: SmartInsightsStatus): void {
    this.reactData = projectToReactData(days);
    this.reactStatus = status;
  }

  @action
  fetchArchived(stayDate: string, subscriptionId?: string): void {
    void this.getArchivedInsightsTask.perform(stayDate, subscriptionId);
  }

  @action
  getHasSeenDictFromLocalStorage(): Record<string, string> {
    const lastViewedDictString = this.localStorage.getItem(
      SMART_INSIGHTS_LAST_VIEWED_LOCAL_STORAGE_KEY,
    );

    const isStringRecord = (
      value: unknown,
    ): value is Record<string, string> => {
      if (typeof value !== 'object' || value === null) {
        return false;
      }
      return Object.values(value).every((val) => typeof val === 'string');
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const lastViewedDict = JSON.parse(lastViewedDictString || '{}');
    if (!lastViewedDict || !isStringRecord(lastViewedDict)) {
      return {};
    }

    return lastViewedDict;
  }

  @action storeHasSeenDictInLocalStorage(): void {
    this.hasDismissedNotificationBubble = true;

    if (!this.localStorageHotelOrMarketId) {
      return;
    }

    const lastViewedDict = this.getHasSeenDictFromLocalStorage();

    lastViewedDict[this.localStorageHotelOrMarketId] = dayjs().toISOString();
    this.localStorage.setItem(
      SMART_INSIGHTS_LAST_VIEWED_LOCAL_STORAGE_KEY,
      JSON.stringify(lastViewedDict),
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'smart-insights': SmartInsightsService;
  }
}
